
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { StatsCards } from "./dashboard/StatsCards";
import { TimeEntriesSection } from "./dashboard/TimeEntriesSection";
import { DashboardHeader } from "./dashboard/DashboardHeader";
import { WelcomeFlow } from "./onboarding/WelcomeFlow";
import { UsageIndicator } from "./freemium/UsageIndicator";
import { UpgradePromptManager } from "./freemium/UpgradePromptManager";
import { WelcomeBanner } from "./WelcomeBanner";
import { TeamMemberDashboard } from "./team/TeamMemberDashboard";
import { useFreemiumLimits } from "@/hooks/useFreemiumLimits";
import { useDashboardProjects } from "@/hooks/useDashboardProjects";
import { useTimeEntries } from "@/hooks/useTimeEntries";
import { useMyTeamProjects } from "@/hooks/useMyTeamProjects";

export function Dashboard() {
  const { session } = useAuth();
  const [showWelcomeFlow, setShowWelcomeFlow] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | "all">("all");
  const { data: limits } = useFreemiumLimits();
  const { projects } = useDashboardProjects();
  const { entries: timeEntries, isLoadingEntries, handleAddEntry, handleUpdateEntry, handleDeleteEntry } = useTimeEntries();
  const { data: myTeamProjects = [] } = useMyTeamProjects();

  // Check if user should see welcome flow
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('onboardingComplete');
    const isNewUser = session && !hasSeenOnboarding;
    
    if (isNewUser) {
      setShowWelcomeFlow(true);
    }
  }, [session]);

  // Calculate stats using the correct data structure
  const filteredEntries = selectedProjectId === "all" 
    ? timeEntries 
    : timeEntries.filter(entry => entry.projectId === selectedProjectId);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todayEntries = filteredEntries.filter(entry => {
    const entryDate = new Date(entry.date);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate.getTime() === today.getTime();
  });

  const totalMinutes = filteredEntries.reduce((acc, entry) => {
    return acc + entry.minutes;
  }, 0);

  const todayMinutes = todayEntries.reduce((acc, entry) => {
    return acc + entry.minutes;
  }, 0);

  const projectStats = filteredEntries.reduce((acc, entry) => {
    acc[entry.projectId] = (acc[entry.projectId] || 0) + entry.minutes;
    return acc;
  }, {} as Record<string, number>);

  // Calculate billing accurately using actual project rates
  const DEFAULT_RATE = 75;
  let totalBilling = 0;
  let totalBillableMinutes = 0;
  let entriesWithoutProject = 0;

  filteredEntries.forEach(entry => {
    const isBillable = entry.is_billable ?? true;
    if (!isBillable) return;

    const project = projects.find(p => p.id === entry.projectId);
    const hours = entry.minutes / 60;
    
    if (project?.hourlyRate) {
      totalBilling += hours * project.hourlyRate;
      totalBillableMinutes += entry.minutes;
    } else if (!entry.projectId) {
      totalBilling += hours * DEFAULT_RATE;
      totalBillableMinutes += entry.minutes;
      entriesWithoutProject++;
    }
  });

  const selectedProject = projects.find(p => p.id === selectedProjectId);
  const hourlyRate = totalBillableMinutes > 0 
    ? (totalBilling / (totalBillableMinutes / 60)) // Weighted average
    : selectedProject?.hourlyRate || DEFAULT_RATE;

  const stats = {
    totalHours: Math.floor(totalMinutes / 60),
    totalMinutes: totalMinutes % 60,
    todayHours: Math.floor(todayMinutes / 60),
    todayMinutes: todayMinutes % 60,
    totalEntries: filteredEntries.length,
    projectStats,
    totalBilling,
    hourlyRate,
    selectedProject,
    entriesWithoutProject
  };

  const handleUpgrade = () => {
    setShowUpgradePrompt(true);
  };

  const handleGetStarted = () => {
    setShowWelcomeFlow(true);
  };

  if (!session) {
    return null;
  }

  // Check if user is completely new (no projects, no time entries)
  const isCompletelyNewUser = projects.length === 0 && timeEntries.length === 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <DashboardHeader 
        selectedProjectId={selectedProjectId}
        setSelectedProjectId={setSelectedProjectId}
        projects={projects}
      />
      
      {/* Welcome Banner for New Users */}
      {isCompletelyNewUser && !showWelcomeFlow && (
        <WelcomeBanner
          title="Welcome to Hour Tracker! 🎉"
          description="You're now on the free plan with access to all core features. Start tracking your first project!"
          ctaLabel="Get Started"
          onCtaClick={handleGetStarted}
        />
      )}
      
      {/* Show team projects if user is a member of any */}
      {myTeamProjects.length > 0 && (
        <TeamMemberDashboard />
      )}
      
      <StatsCards stats={stats} projects={projects} />
      <TimeEntriesSection 
        entries={timeEntries}
        projects={projects}
        isLoading={isLoadingEntries}
        onAddEntry={handleAddEntry}
        onUpdateEntry={handleUpdateEntry}
        onDeleteEntry={handleDeleteEntry}
      />
      
      {/* Welcome Flow for New Users */}
      {showWelcomeFlow && (
        <WelcomeFlow onComplete={() => setShowWelcomeFlow(false)} />
      )}
      
      {/* Upgrade Prompt Manager */}
      <UpgradePromptManager 
        triggerType={showUpgradePrompt ? 'manual' : undefined}
        onClose={() => setShowUpgradePrompt(false)}
      />
    </div>
  );
}
